import React, { useEffect, useMemo, useState } from "react";
import { Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import DrawerLayout from "./components/DrawerLayout";
import DashboardPage from "./pages/DashboardPage";
import AppsPage from "./pages/AppsPage";
import AgentsPage from "./pages/AgentsPage";
import EditorPage from "./pages/EditorPage";
import PlanPage from "./pages/PlanPage";
import LoginPage from "./pages/LoginPage";
import ProfilePage from "./pages/ProfilePage";
import SignupClosedPage from "./pages/SignupClosedPage";
import ChatDevPage from "./pages/ChatDevPage";
import EditAgentPage from "./pages/EditAgentPage";
import BuildWebUiDevPage from "./pages/BuildWebUiDevPage";
import AppPreviewPage from "./pages/AppPreviewPage";
import { auth, db } from "./services/firebase";
import { getPlanInfo } from "./services/functionsClient";
import "./App.css";

function ProtectedRoute({ user, initializingProfile, planInfo, planLoading, children }) {
  const location = useLocation();
  const billingPlanMissing = planInfo ? planInfo.billingInfo?.plan == null : false;
  const onPlanPage = location.pathname.startsWith("/plan");

  console.log("[ProtectedRoute] render", {
    initializingProfile,
    planLoading,
    hasUser: Boolean(user),
    billingPlanMissing,
    onPlanPage,
    pathname: location.pathname
  });

  if (initializingProfile || planLoading) {
    console.log("[ProtectedRoute] showing splash (initializingProfile)");
    return <div className="splash">Loading experience...</div>;
  }

  if (!user && !initializingProfile) {
    console.log("[ProtectedRoute] redirecting to /login (no user)");
    return <Navigate to="/login" replace />;
  }

  if (!user) {
    console.log("[ProtectedRoute] showing splash (waiting for user)");
    return <div className="splash">Loading experience...</div>;
  }

  if (billingPlanMissing && !onPlanPage) {
    console.log("[ProtectedRoute] redirecting to /plan (missing billing plan)");
    return <Navigate to="/plan" replace />;
  }

  return children;
}

function AppShell({ user }) {
  return (
    <DrawerLayout user={user}>
      <Outlet />
    </DrawerLayout>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [planInfo, setPlanInfo] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);

  useEffect(() => {
    let unsubscribeProfile;

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      console.log("[App] auth state changed", {
        hasUser: Boolean(currentUser),
        uid: currentUser?.uid
      });
      setUser(currentUser);

      if (unsubscribeProfile) {
        console.log("[App] cleaning up previous profile listener");
        unsubscribeProfile();
      }

      if (!currentUser) {
        console.log("[App] no user after auth change; clearing profile state");
        setUserProfile(null);
        setProfileLoading(false);
        setInitializing(false);
        return;
      }

      console.log("[App] setting up profile listener", { uid: currentUser.uid });
      setProfileLoading(true);

      const userRef = doc(db, "users", currentUser.uid);
      unsubscribeProfile = onSnapshot(
        userRef,
        (profileSnapshot) => {
          console.log("[App] profile snapshot received", {
            exists: profileSnapshot.exists(),
            uid: profileSnapshot.id
          });

          if (!profileSnapshot.exists()) {
            console.log("[App] profile does not exist; clearing state");
            setUserProfile(null);
            setProfileLoading(false);
            setInitializing(false);
            return;
          }

          const profileData = profileSnapshot.data();
          console.log("[App] profile data", profileData);
          setUserProfile({
            id: profileSnapshot.id,
            ...profileData,
            billingInfo: profileData?.billingInfo ?? null,
            basicInfo: profileData?.basicInfo ?? {}
          });
          setProfileLoading(false);
          setInitializing(false);
        },
        () => {
          console.log("[App] profile snapshot error; keeping previous profile");
          // If the profile fails to load, keep the previous profile instead of
          // overwriting it with null so we don't discard valid billing data.
          setUserProfile((prevProfile) => prevProfile ?? null);
          setProfileLoading(false);
          setInitializing(false);
        }
      );
    });

    return () => {
      if (unsubscribeProfile) {
        unsubscribeProfile();
      }
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!user?.uid) {
      console.log("[App] no user; clearing plan info");
      setPlanInfo(null);
      setPlanLoading(false);
      return;
    }

    let isMounted = true;
    setPlanLoading(true);
    console.log("[App] fetching plan info", { uid: user.uid });

    getPlanInfo({ userId: user.uid })
      .then((response) => {
        if (!isMounted) return;
        console.log("[App] plan info fetched", response);
        setPlanInfo(response);
      })
      .catch((error) => {
        if (!isMounted) return;
        console.error("[App] failed to fetch plan info", error);
        setPlanInfo(null);
      })
      .finally(() => {
        if (!isMounted) return;
        setPlanLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [user?.uid]);

  const hydratedUser = useMemo(() => {
    if (!user) return null;

    const profileData = userProfile ?? {};
    const billingInfo = profileData.billingInfo ?? null;
    const basicInfo = profileData.basicInfo ?? {};

    console.log("[App] hydrating user", {
      uid: user.uid,
      hasProfileData: Boolean(userProfile),
      hasBillingInfo: Boolean(billingInfo),
      hasBasicInfo: Boolean(basicInfo)
    });

    return {
      ...profileData,
      uid: user.uid,
      email: user.email,
      billingInfo,
      basicInfo
    };
  }, [user, userProfile]);

  if (initializing) {
    return <div className="splash">Loading experience...</div>;
  }

  return (
    <Routes>
      <Route path="/app/:appId" element={<AppPreviewPage />} />
      <Route path="/login" element={<LoginPage user={hydratedUser} />} />
      <Route path="/signup-closed" element={<SignupClosedPage />} />
      <Route
        element={
          <ProtectedRoute
            user={hydratedUser}
            initializingProfile={profileLoading}
            planInfo={planInfo}
            planLoading={planLoading}
          >
            <AppShell user={hydratedUser} />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/apps" element={<AppsPage user={hydratedUser} />} />
        <Route path="/agents" element={<AgentsPage user={hydratedUser} />} />
        <Route path="/apps/:appId" element={<EditorPage />} />
        <Route path="/plan" element={<PlanPage user={hydratedUser} />} />
        <Route path="/chat-dev" element={<ChatDevPage />} />
        <Route path="/build-agent-dev" element={<EditAgentPage />} />
        <Route path="/edit-agent/:agentId" element={<EditAgentPage />} />
        <Route path="/build-webui-dev" element={<BuildWebUiDevPage />} />
        <Route path="/profile" element={<ProfilePage />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default App;
